import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Card } from '../../../ui/card';
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  UserCheck,
  CreditCard,
  Pause,
  ChevronRight,
  ShieldAlert,
  Inbox,
} from 'lucide-react';

const iconMap = {
  Clock: Clock,
  AlertTriangle: AlertTriangle,
  Pause: Pause,
  CreditCard: CreditCard,
  UserCheck: UserCheck,
};

const severityStyles = {
  high: {
    bg: 'bg-red-50/80',
    border: 'border-red-200/60',
    icon: 'text-red-500',
    badge: 'bg-red-100 text-red-700',
  },
  medium: {
    bg: 'bg-amber-50/80',
    border: 'border-amber-200/60',
    icon: 'text-amber-500',
    badge: 'bg-amber-100 text-amber-700',
  },
  low: {
    bg: 'bg-blue-50/80',
    border: 'border-blue-200/60',
    icon: 'text-blue-500',
    badge: 'bg-blue-100 text-blue-700',
  },
};

const AlertItemRow = ({ alert, onClick }) => {
  const Icon = iconMap[alert.icon] || AlertTriangle;
  const severity = severityStyles[alert.severity] || severityStyles.medium;

  return (
    <motion.button
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      onClick={onClick}
      className={`w-full flex items-center gap-3 p-3 rounded-xl border ${severity.border} ${severity.bg} hover:shadow-sm transition-all group`}
    >
      <div className="p-2 rounded-lg bg-white shadow-sm border border-stone-100">
        <Icon className={`w-4 h-4 ${severity.icon}`} />
      </div>
      <div className="flex-1 text-left">
        <p className="text-sm font-semibold text-gray-800">{alert.label}</p>
        <p className="text-xs text-gray-500">Click to review</p>
      </div>
      <span
        className={`px-2.5 py-1 text-xs font-bold rounded-lg ${severity.badge}`}
      >
        {alert.count}
      </span>
      <ChevronRight className="w-4 h-4 text-gray-400 group-hover:translate-x-0.5 transition-transform" />
    </motion.button>
  );
};

export function ActionRequiredPanel({ alerts, loading }) {
  const navigate = useNavigate();

  if (loading || !alerts) {
    return (
      <Card className="p-5 bg-white border-stone-200/80">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-5 h-5 bg-stone-100 rounded animate-pulse" />
          <div className="h-5 w-32 bg-stone-100 rounded animate-pulse" />
        </div>
        <div className="space-y-3">
          {[...Array(3)].map((_, i) => (
            <div
              key={i}
              className="h-16 bg-stone-50 rounded-xl animate-pulse"
            />
          ))}
        </div>
      </Card>
    );
  }

  const hasAlerts = alerts.totalAlerts > 0;

  const handleAlertClick = alert => {
    if (alert.route) {
      navigate(alert.route);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: 0.2 }}
    >
      <Card className="p-5 h-full bg-white border-stone-200/80">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div
              className={`p-1.5 rounded-lg ${hasAlerts ? 'bg-red-50' : 'bg-stone-50'}`}
            >
              <ShieldAlert
                className={`w-4 h-4 ${hasAlerts ? 'text-red-500' : 'text-gray-400'}`}
              />
            </div>
            <h3 className="font-semibold text-gray-900 font-outfit">
              Action Required
            </h3>
          </div>
          {hasAlerts && (
            <span className="px-2.5 py-1 bg-[#F2575F] text-white text-xs font-bold rounded-lg ">
              {alerts.totalAlerts} pending
            </span>
          )}
        </div>

        {hasAlerts ? (
          <div className="space-y-2">
            {alerts.alerts.map((alert, index) => (
              <AlertItemRow
                key={alert.type || index}
                alert={alert}
                onClick={() => handleAlertClick(alert)}
              />
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <div className="relative mb-4">
              <div className="relative p-4 bg-emerald-50 rounded-2xl border border-emerald-100">
                <CheckCircle2 className="w-10 h-10 text-emerald-500" />
              </div>
            </div>
            <p className="text-base font-semibold text-gray-800">All Clear!</p>
            <p className="text-sm text-gray-500 mt-1 max-w-[200px]">
              No critical actions required right now. Great job staying on top
              of things!
            </p>
            <div className="flex items-center gap-2 mt-4 px-3 py-1.5 bg-emerald-50 rounded-lg border border-emerald-100">
              <Inbox className="w-3.5 h-3.5 text-emerald-600" />
              <span className="text-xs font-medium text-emerald-700">
                0 pending items
              </span>
            </div>
          </div>
        )}
      </Card>
    </motion.div>
  );
}

export default ActionRequiredPanel;
